import { Injectable } from "@angular/core";
import { Observable, retry, catchError } from "rxjs";
import { CreditCard, CreditCardType } from "src/models/creditCard";
import { ResultSet } from "src/models/resultset";
import { AppSettings } from "../app-settings";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { DonationRequest } from "src/models/donationRequest";
import { EventPaymentRequest, PaymentTypeId } from "src/models/eventPaymentRequest";
import { EventCardRefundRequest } from "src/models/eventCardRefundRequest";
import { PaymentHistory } from "src/models/paymentHistory";
import { RecurringPayment } from "src/models/RecurringPayment";

@Injectable({
    providedIn: 'root'
})
export class PaymentService {
    constructor(private http: HttpClient, private appSettings: AppSettings) { }

    httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json'
        })
    };

    getPayments(): Observable<CreditCard[]> {
        let url = this.appSettings.apiBaseUrl() + 'Payment/GetUserPayment';

        return this.http.get<CreditCard[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 1000 })
            );
    }

    setPaymentAsDefault(paymentId: number): Observable<CreditCard[]> {
        let url = this.appSettings.apiBaseUrl() + 'Payment/SetPaymentAsDefault?paymentId=' + paymentId;

        return this.http.get<CreditCard[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 1000 })
            );
    }

    removePayment(paymentId: number): Observable<ResultSet<number>> {
        let url = this.appSettings.apiBaseUrl() + 'Payment/RemoveUserPayment?paymentId=' + paymentId;

        return this.http.get<ResultSet<number>>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 1000 })
            );
    }

    savePayment(newPayemnt: CreditCard): Observable<ResultSet<CreditCard>> {
        let url = this.appSettings.apiBaseUrl() + 'Payment/SavePayment';

        return this.http.post<ResultSet<CreditCard>>(url, newPayemnt, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 1000 })
            );
    }

    getCreditCardType(cardNumber, expiry, cvv): Observable<CreditCardType> {
        let url = this.appSettings.apiBaseUrl() + `Payment/GetCreditCardType?cardNumber=${cardNumber}&expiry=${expiry}&cvv=${cvv}`;

        return this.http.get<CreditCardType>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 1000 })
            );
    }

    donate(donationRequest: DonationRequest): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Payment/Donate`;
        return this.http.post<boolean>(url, donationRequest, this.httpOptions)
    }

    eventPayment(eventPaymentRequest: EventPaymentRequest, paymentTypeId: PaymentTypeId): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Payment/EventPayment?paymentTypeId=${paymentTypeId}`;
        return this.http.post<boolean>(url, eventPaymentRequest, this.httpOptions)
    }

    oneTimePayment(creditCard: CreditCard, emailaddress: string, name: string, amount: string, paymentTypeId: PaymentTypeId): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Payment/OneTimePayment?emailaddress=${emailaddress}&name=${name}&amount=${amount}&paymentTypeId=${paymentTypeId}`;
        return this.http.post<boolean>(url, creditCard, this.httpOptions)
    }

    processRefund(request: EventCardRefundRequest): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Payment/Refund`;
        return this.http.post<boolean>(url, request, this.httpOptions)
    }

    processCashRefund(amount: number, profileId: number, paymentCartId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Payment/CashRefund?amount=${amount}&profileId=${profileId}&paymentCartId=${paymentCartId}`;
        return this.http.get<boolean>(url, this.httpOptions)
    }

    getPaymentHistory(year: number, paymentTypeId: string): Observable<PaymentHistory[]> {
        let url = this.appSettings.apiBaseUrl() + `Payment/GetPaymentHistory?year=${year}&paymentTypeId=${paymentTypeId}`;
        return this.http.get<PaymentHistory[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 })
            );
    }

    getRecurringPayments(): Observable<RecurringPayment> {
        let url = this.appSettings.apiBaseUrl() + `Payment/GetRecurringPayment`;
        return this.http.get<RecurringPayment>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 500 })
            );
    }

    saveRecurringPayment(recurringPayment: RecurringPayment): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + `Payment/SaveRecurringPayment`;
        return this.http.post<number>(url, recurringPayment, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 500 })
            );
    }
}